import type { QuestionType } from './types';

type Props = {
  onSelect: (type: QuestionType) => void;
  onClose: () => void;
};

type Item = {
  type: QuestionType;
  label: string;
  desc: string;
  icon: React.ReactNode;
};

const ITEMS: Item[] = [
  {
    type: 'text',
    label: '填空题',
    desc: '单行文本',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 8h18M3 16h12" />
      </svg>
    ),
  },
  {
    type: 'longtext',
    label: '开放题',
    desc: '多行文本',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M7 9h10M7 13h10M7 17h6" />
      </svg>
    ),
  },
  {
    type: 'single',
    label: '单选题',
    desc: '单选一项',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="4" fill="currentColor" />
      </svg>
    ),
  },
  {
    type: 'multi',
    label: '多选题',
    desc: '可多选',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    type: 'ranking',
    label: '排序题',
    desc: '点击置顶排序',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 6h11M10 12h11M10 18h11" />
        <path d="M3 9l3-3 3 3M6 6v12" />
      </svg>
    ),
  },
  {
    type: 'slider',
    label: '滑动条',
    desc: '数值范围',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M3 12h12" />
        <circle cx="17" cy="12" r="3" fill="currentColor" />
        <path d="M20 12h1" />
      </svg>
    ),
  },
  {
    type: 'select',
    label: '下拉框',
    desc: '下拉单选',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M14 11l3 3 3-3" transform="translate(-3 -1)" />
      </svg>
    ),
  },
  {
    type: 'matrix',
    label: '矩阵量表',
    desc: '行×列打分',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
      </svg>
    ),
  },
  {
    type: 'matrixtext',
    label: '矩阵填空',
    desc: '多行填空',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M3 10h18M3 15h18M10 4v16M13 7.5h5M13 12.5h5M13 17.5h5" />
      </svg>
    ),
  },
  {
    type: 'matrixslider',
    label: '矩阵滑动条',
    desc: '每行一个滑块',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7h10M3 12h6M3 17h12" />
        <circle cx="16" cy="7" r="2.2" fill="currentColor" />
        <circle cx="12" cy="12" r="2.2" fill="currentColor" />
        <circle cx="18" cy="17" r="2.2" fill="currentColor" />
        <path d="M18.2 7h2.8M14.2 12h6.8M20.2 17h.8" />
      </svg>
    ),
  },
  {
    type: 'file',
    label: '上传附件',
    desc: '图片 / PDF',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 16V4M7 9l5-5 5 5" />
        <path d="M4 20h16" />
      </svg>
    ),
  },
  {
    type: 'statement',
    label: '标题说明',
    desc: '不可作答',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 6h16M4 12h12M4 18h8" />
      </svg>
    ),
  },
];

export default function AddQuestionModal({ onSelect, onClose }: Props) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-add-question" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>添加题目</h3>
          <button className="icon-btn" onClick={onClose} title="关闭">✕</button>
        </div>
        <div className="add-q-grid">
          {ITEMS.map((it) => (
            <button
              key={it.type}
              className="add-q-item"
              onClick={() => onSelect(it.type)}
            >
              <div className="add-q-icon">{it.icon}</div>
              <div className="add-q-text">
                <div className="add-q-label">{it.label}</div>
                <div className="add-q-desc">{it.desc}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose}>取消</button>
        </div>
      </div>
    </div>
  );
}